"use client";

import { useRef, useState } from "react";

import { Field } from "@base-ui/react/field";
import { Input } from "@base-ui/react/input";

import { cn } from "@marketing-sites/utils/cn";
import {
  composeValidate,
  type ValidationRule,
} from "@marketing-sites/utils/validation";

import { Button } from "../../primitives/button";
import { Form } from "../../primitives/form";

export type EmailCaptureFieldProps = {
  /** Seeds the field, e.g. with an address already known for this visitor. */
  defaultValue?: string;
  /** Called with the address once it has passed validation. */
  onSubmit: (email: string) => void;
  /** What the address must satisfy. The first failing rule is the one shown. */
  validations?: ValidationRule[];
  requiredMessage?: string;
  /** Disables submitting and swaps the button label while one is in flight. */
  pending?: boolean;
  placeholder?: string;
  submitLabel?: string;
  pendingLabel?: string;
  /** Names the input for assistive tech, since no label is shown. */
  label?: string;
  /** Identifies the submit button in CTA-click reporting. */
  analyticsId: string;
  /** CTA-click label, when it should differ from the button text. */
  analyticsLabel?: string;
  className?: string;
};

/**
 * An email address and a submit button sharing one bordered container, for a
 * marketing page that wants to collect an address before sending someone on.
 *
 * Validation and error copy belong to the field, so `onSubmit` only ever sees
 * an address that passed. The caller still owns `pending`, which depends on
 * what it does with the address rather than on anything the field can see.
 */
export function EmailCaptureField({
  defaultValue = "",
  onSubmit,
  validations,
  requiredMessage = "This field is required",
  pending = false,
  placeholder = "Work email",
  submitLabel = "Start free trial",
  pendingLabel = "Please wait…",
  label = "Work email",
  analyticsId,
  analyticsLabel,
  className,
}: EmailCaptureFieldProps) {
  const submitButton = useRef<HTMLButtonElement>(null);
  const [email, setEmail] = useState(defaultValue);

  /**
   * Every message comes from a rule, so exactly one error can be showing and
   * its wording is ours. The browser's own check would otherwise add a second
   * message we can neither translate nor word, in a field whose rules are
   * stricter than the browser's anyway.
   */
  const rules: ValidationRule[] = [
    { match: (value) => value.length === 0, message: requiredMessage },
    ...(validations ?? []),
  ];
  const validateRules = composeValidate(rules);

  /**
   * What the rules judge and what `onSubmit` receives, so a length rule can't
   * reject an address that only overruns with the spaces around it. The
   * visitor still sees exactly what they typed.
   */
  const address = email.trim();

  return (
    <Form.Root
      validationMode="onSubmit"
      onSubmit={(event) => {
        event.preventDefault();
        if (pending) return;
        onSubmit(address);
      }}
      className={cn("w-full", className)}
    >
      {/* Focus and invalid states sit on the container: the input is
          borderless inside it, so its own ring and red border would be lost.
          The ring is scoped to the input so the button's own focus doesn't
          light up the whole box. The button is taller than the input, so the
          input centres against it — except while an error is showing, when
          top-aligning keeps the growing input column from moving the button. */}
      <div className="border-off-black/20 has-[[data-invalid]]:border-error has-[input:focus-visible]:outline-off-black flex flex-col gap-2 rounded-sm border bg-white p-1.5 has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-offset-2 md:flex-row md:items-center md:p-2 md:has-[[data-invalid]]:items-start">
        {/* Base UI's Field rather than the `Input` primitive: that one also
            brings `.input`, which is styled as a bordered field in its own
            right and cannot be neutralised inside this box. */}
        <Field.Root
          name="email"
          validate={() => validateRules?.(address) ?? null}
          className="min-w-0 flex-1"
        >
          <Input
            type="text"
            aria-required
            inputMode="email"
            autoComplete="email"
            spellCheck={false}
            aria-label={label}
            placeholder={placeholder}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.nativeEvent.isComposing) {
                event.preventDefault();
                // Clicking the button, rather than submitting the form, keeps Enter and a real click on one CTA-click event.
                submitButton.current?.click();
              }
            }}
            className="text-body placeholder:text-current/40 w-full border-none bg-transparent px-3 py-2 outline-none"
          />
          {/* `role="alert"` because Base UI renders the message with no role
              of its own, so it would appear without being announced. */}
          <Field.Error role="alert" className="label-error px-3 pt-1" />
        </Field.Root>
        <Button
          ref={submitButton}
          type="submit"
          disabled={pending}
          analyticsId={analyticsId}
          analyticsLabel={analyticsLabel ?? submitLabel}
          className="shrink-0 whitespace-nowrap"
        >
          {pending ? pendingLabel : submitLabel}
        </Button>
      </div>
    </Form.Root>
  );
}
